import {UserAudioSource} from '@Libraries/user-audio-library';
import {CommonMethods} from '@PosterWhiteboard/common-methods';
import type {AudioClips} from '@PosterWhiteboard/classes/audio-clips/audio-clips.class';
import {SyncAudioToPageClock} from '@PosterWhiteboard/classes/audio-clips/sync-audio-to-page-clock.class';
import {AudioPlayer} from '@Libraries/audio-player/audio-player.class';
import type {TimelineResizeBounds} from '@PosterWhiteboard/page/timeline-group-gestures.library';
import {getAudioTimelineResizeBounds} from '@PosterWhiteboard/page/timeline-group-gestures.library';
import type {AudioPlayerObject, FadeData} from '@Libraries/audio-player/audio-player.types';
import {getDefaultFadeDurationForAudioDuration} from '@Libraries/poster-audio-item.library';
import {EditorMediaEvent, trackPosterBuilderGA4Events} from '@Libraries/ga-events';
import type {DeepPartial} from '@/global';
import {quantizeTimeSeconds} from '@Utils/math.util';

export interface AudioItemAudioPlayerObject extends AudioPlayerObject {
  isPlaying: boolean;
}

export interface AudioItemUpdateFromObjectProps {
  fireEvent?: boolean;
  undoable?: boolean;
  updateRedux?: boolean;
}

export interface AudioItemObject {
  uid: string;
  hashedFilename: string;
  audioPlayer: AudioItemAudioPlayerObject;
  source: UserAudioSource;
  onPageStartTime: number;
  name: string;
  timelineSubRow: number;
}

export class AudioItem extends CommonMethods {
  public audioClips: AudioClips;
  public uid = '';
  public hashedFilename = '';
  public source = UserAudioSource.UPLOAD;
  public onPageStartTime = 0;
  public name = '';
  public timelineSubRow = 0;
  public audioPlayer: AudioPlayer;
  private readonly syncToPageClock: SyncAudioToPageClock;

  public constructor(audioPlaylist: AudioClips) {
    super();
    this.audioClips = audioPlaylist;
    this.syncToPageClock = new SyncAudioToPageClock(this);
    this.syncToPageClock.initSyncToPageClock();

    this.audioPlayer = new AudioPlayer();
    this.initAudioPlayerEvents();
  }

  public async init(): Promise<void> {
    await this.audioPlayer.init();
  }

  public async updateFromObject(obj: DeepPartial<AudioItemObject>, {updateRedux = true, fireEvent = true, undoable = true}: AudioItemUpdateFromObjectProps = {}): Promise<void> {
    const {audioPlayer, ...audioItemObject} = obj;
    this.copyVals(audioItemObject);

    if (audioPlayer) {
      await this.audioPlayer.updateFromObject(audioPlayer);
    }

    if (this.shouldBePlaying() && !this.audioPlayer.isPlaying()) {
      this.play();
    }

    if (undoable) {
      this.audioClips.page.poster.history.addPosterHistory();
    }
    if (fireEvent) {
      this.audioClips.page.poster.fire('modified');
    }
    if (updateRedux) {
      this.audioClips.page.poster.redux.updateReduxData();
    }
  }

  public clipAudioExceedingPageDuration(): void {
    const currentPageDuration = this.audioClips.page.getDuration();
    this.audioPlayer.trim = {
      startTime: this.audioPlayer.getTrimmedStartTime(),
      isTrimmed: true,
      endTime: this.getEndTimeAccordingToPageDuration(currentPageDuration),
    };
  }

  public async onToggleFade(): Promise<void> {
    let fadeData: FadeData = {
      fadeInDuration: getDefaultFadeDurationForAudioDuration(this.audioPlayer.getPlaybackDuration()),
      fadeOutDuration: getDefaultFadeDurationForAudioDuration(this.audioPlayer.getPlaybackDuration()),
    };

    if (this.audioPlayer.fade.fadeInDuration !== 0 || this.audioPlayer.fade.fadeOutDuration !== 0) {
      fadeData = {
        fadeInDuration: 0,
        fadeOutDuration: 0,
      };
      trackPosterBuilderGA4Events(EditorMediaEvent.Audio_Fade_Removed);
    } else {
      trackPosterBuilderGA4Events(EditorMediaEvent.Audio_Fade_Add);
    }

    await this.updateFromObject({
      audioPlayer: {
        fade: fadeData,
      },
    });
  }

  public shouldBePlaying(): boolean {
    return this.audioClips.page.isPlaying && this.isWithinPageCurrentTime();
  }

  public isWithinPageCurrentTime(): boolean {
    const pageCurrentTime = this.audioClips.page.clock.getCurrentTime();
    return pageCurrentTime >= this.onPageStartTime && pageCurrentTime < this.getOnPageEndTime();
  }

  public getOnPageEndTime(): number {
    return quantizeTimeSeconds(this.audioPlayer.getPlaybackDuration() + this.onPageStartTime);
  }

  public getTimelineResizeBounds(): TimelineResizeBounds {
    return getAudioTimelineResizeBounds({startTime: this.onPageStartTime, endTime: this.getOnPageEndTime()}, this.audioPlayer);
  }

  public override copyVals(obj: DeepPartial<AudioItemObject>): void {
    const {audioPlayer, ...itemObj} = obj;
    super.copyVals(itemObj);
    if (audioPlayer) {
      this.audioPlayer.copyVals(audioPlayer);
    }
  }

  public toObject(): AudioItemObject {
    return {
      uid: this.uid,
      hashedFilename: this.hashedFilename,
      source: this.source,
      audioPlayer: {
        isPlaying: this.audioPlayer.isPlaying(),
        ...this.audioPlayer.toObject(),
      },
      onPageStartTime: this.onPageStartTime,
      name: this.name,
      timelineSubRow: this.timelineSubRow,
    };
  }

  public onRemove(): void {
    this.syncToPageClock.unload();
    this.audioPlayer.unload();
  }

  public async seekToPageTime(pageTime: number): Promise<void> {
    if (pageTime < this.onPageStartTime || pageTime > this.getOnPageEndTime()) {
      return;
    }

    await this.audioPlayer.seekToPlaybackTime(pageTime - this.onPageStartTime);
  }

  public play(): void {
    if (!this.shouldBePlaying()) {
      return;
    }

    this.audioPlayer.play();
  }

  private initAudioPlayerEvents = (): void => {
    this.audioPlayer.on('onPlay', () => {
      this.audioClips.page.poster.redux.updateAudioItemIsPlayingInRedux(this.uid, true);
    });
    this.audioPlayer.on('onPause', () => {
      this.audioClips.page.poster.redux.updateAudioItemIsPlayingInRedux(this.uid, false);
    });
    this.audioPlayer.on('onStop', () => {
      this.audioClips.page.poster.redux.updateAudioItemIsPlayingInRedux(this.uid, false);
    });
    this.audioPlayer.on('onEnd', () => {
      this.audioClips.page.poster.redux.updateAudioItemIsPlayingInRedux(this.uid, false);
    });
  };

  public getEndTimeAccordingToPageDuration(pageDuration: number): number {
    const onPageEndTime = this.getOnPageEndTime();

    // The effective end, not the raw field: an untrimmed clip stores `endTime: 0`,
    // and the caller stamps this back as `isTrimmed: true` — returning the raw 0
    // would collapse a clip that already fits the page to zero length.
    if (onPageEndTime <= pageDuration) {
      return this.audioPlayer.getTrimmedEndTime();
    }

    const newDuration = (pageDuration - this.onPageStartTime) * this.audioPlayer.speed;
    return quantizeTimeSeconds(this.audioPlayer.getTrimmedStartTime() + newDuration);
  }
}

export const createAudioItemFromObject = async (audioPlaylist: AudioClips, audioItemObject: DeepPartial<AudioItemObject>): Promise<AudioItem> => {
  const audioItem = new AudioItem(audioPlaylist);
  audioItem.copyVals(audioItemObject);
  await audioItem.init();
  return audioItem;
};
